import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  useTheme,
  TextField,
} from "@mui/material";
import { useCookies } from "react-cookie";
import { useTranslation } from "react-i18next";
import { CCMPatient } from "../../interfaces/CCMPatient";
import { Device } from "../../interfaces/Device";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CancelIcon from "@mui/icons-material/Cancel";
import RefreshIcon from "@mui/icons-material/Refresh";
import { RPM_HISTORY_DAYS } from "../../components/CMS/MainScreen";
import PatientService from "../../services/PatientService";
import { format, subDays } from "date-fns";
import { MeasurementTrendDialog } from "./MeasurementTrendDialog";
import { MeasurementListDialog } from "./MeasurementListDialog";
import { MeasurementCalendarDialog } from "./MeasurementCalendarDialog";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import TimeTracker from "./TimeTracker"; // Adjust the path if necessary
import { MeasurementGauge } from "./MeasurementGauge";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import ElapsedTimeWidget from "./ElapsedTimeWidget";
import EngagementDetailsDialog from "./EngagementDetailsDialog";
import Typography from "@mui/material/Typography";
import VideocamIcon from "@mui/icons-material/Videocam";
import { JitsiMeeting } from "@jitsi/react-sdk";
import { UserContext } from "../../context/UserContext";

// --- Existing helper for traffic light icons ---
export const getTrafficLight = (status: string | null) => {
  switch (status) {
    case "OK":
      return <CheckCircleIcon sx={{ color: "green", fontSize: 20 }} />;
    case "NOT OK":
    case "PARTIAL":
      return <ErrorIcon sx={{ color: "orange", fontSize: 20 }} />;
    case "SKIPPED":
      return <CancelIcon sx={{ color: "red", fontSize: 20 }} />;
    case "NO PLAN":
    default:
      return <RadioButtonUncheckedIcon sx={{ color: "gray", fontSize: 20 }} />;
  }
};

interface CCMCentralProps {
  organization_id: string;
  patientlist: CCMPatient[];
  devicelist?: Device[];
  filterValue?: number;
  children?: React.ReactNode;
  refreshPatients?: () => void;
}
interface Measurement {
  timestamp: string;
  value: any;
}

interface Deadline {
  local_time: string;
  status: string;
  measurements: Measurement[];
}

export interface Category {
  measurement_type: string;
  deadlines: Deadline[];
}

export const CCMCentral = ({
  organization_id,
  patientlist,
  devicelist,
  filterValue,
  children,
  refreshPatients,
}: CCMCentralProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userCtx = useContext(UserContext);
  const [cookies] = useCookies(["access_token"]);
  const token = cookies.access_token;
  const [patients, setPatients] = useState<CCMPatient[]>(patientlist);
  const [search, setSearch] = useState("");
  const [tableHeight, setTableHeight] = useState("500px"); // Default height
  const [openEngagementDialog, setOpenEngagementDialog] = useState(false);
  // Type the selectedEngagement state as Category[] | null
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(
    null,
  );
  const [selectedPatient, setSelectedPatient] = useState<CCMPatient | null>(
    null,
  );
  const [openTimeTracker, setOpenTimeTracker] = useState(false);
  const [selectedPatientForTracker, setSelectedPatientForTracker] = useState<
    string | null
  >(null);
  const [openTrendDialog, setOpenTrendDialog] = useState(false);
  const [openListDialog, setOpenListDialog] = useState(false);
  const [openCalendarDialog, setOpenCalendarDialog] = useState(false);
  const [openVideoCall, setOpenVideoCall] = useState(false);
  const [selectedEngagementPatient, setSelectedEngagementPatient] = useState<
    string | null
  >(null);
  const [selectedPatientForVideoCall, setSelectedPatientForVideoCall] =
    useState<CCMPatient | null>(null);
  // Temporary feature flag: disable patient search filter UI and behavior
  const ENABLE_PATIENT_FILTER = false;
  const [selectedEngagementDate, setSelectedEngagementDate] =
    useState<string>("");
  const meetingDisplayName =
    userCtx?.user?.username ||
    [userCtx?.user?.first_name, userCtx?.user?.last_name]
      .filter(Boolean)
      .join(" ") ||
    "User";
  const meetingEmail =
    userCtx?.user?.email ||
    (userCtx?.user?.username
      ? `${userCtx.user.username}@sentioweb.com`
      : "user@example.com");

  useEffect(() => {
    console.log("=== CCMCentral: Receiving patientlist ===");
    console.log("patientlist:", patientlist);
    console.log("patientlist length:", patientlist?.length);
    if (patientlist?.length > 0) {
      console.log(
        "Sample patients:",
        patientlist
          .slice(0, 3)
          .map((p) => `${p.first_name} ${p.last_name} (${p.patient_id})`),
      );
    }
    setPatients(patientlist);
  }, [patientlist]);

  useEffect(() => {
    // Adjust table height dynamically based on window size.
    const updateHeight = () => {
      const maxHeight = window.innerHeight - 250;
      setTableHeight(`${maxHeight}px`);
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  // Keep the filter logic in place for later re-enable, but bypass when flag is false
  const filteredPatients = patients.filter(
    (patient: CCMPatient) =>
      patient.patient_id.toLowerCase().includes(search.toLowerCase()) ||
      (patient.patient_groups &&
        patient.patient_groups
          .join(", ")
          .toLowerCase()
          .includes(search.toLowerCase())) ||
      (patient.last_name &&
        patient.last_name.toLowerCase().includes(search.toLowerCase())) ||
      (patient.first_name &&
        patient.first_name.toLowerCase().includes(search.toLowerCase())),
  );
  const displayPatients = ENABLE_PATIENT_FILTER ? filteredPatients : patients;

  const handleTrafficLightClick = (patient: CCMPatient, date: string) => {
    setSelectedEngagementPatient(patient.patient_id);
    setSelectedEngagementDate(date);
    setOpenEngagementDialog(true);
  };

  const handleCloseEngagementDialog = () => {
    setOpenEngagementDialog(false);
  };

  const handleOpenPatientPlan = (patient: CCMPatient) => {
    console.log("Navigating to patient plan for:", patient);
    navigate(`/patient/${patient.patient_id}`);
  };

  const handleOpenTrendDialog = (patientId: string) => {
    setSelectedPatientId(patientId);
    setSelectedPatient(
      patients.find((patient) => patient.patient_id === patientId) || null,
    );
    setOpenTrendDialog(true);
  };

  const handleCloseTrendDialog = () => {
    setOpenTrendDialog(false);
    setSelectedPatientId(null);
  };

  const handleOpenListDialog = (patientId: string) => {
    setSelectedPatientId(patientId);
    setSelectedPatient(
      patients.find((patient) => patient.patient_id === patientId) || null,
    );
    setOpenListDialog(true);
  };

  const handleCloseListDialog = () => {
    setOpenListDialog(false);
    setSelectedPatientId(null);
  };

  const handleOpenCalendarDialog = (patientId: string) => {
    setSelectedPatientId(patientId);
    setSelectedPatient(
      patients.find((patient) => patient.patient_id === patientId) || null,
    );
    setOpenCalendarDialog(true);
  };

  const handleCloseCalendarDialog = () => {
    setOpenCalendarDialog(false);
    setSelectedPatientId(null);
  };

  const handleOpenVideoCall = (patient: CCMPatient) => {
    setSelectedPatientForVideoCall(patient);
    setOpenVideoCall(true);
  };

  const handleCloseVideoCall = () => {
    setOpenVideoCall(false);
    setSelectedPatientForVideoCall(null);
  };

  // The time tracker is opened by setting selectedPatientForTracker and openTimeTracker.

  const handleTimeTrackerClose = async (
    patientId: string | null,
    utc_time: string | null,
  ) => {
    if (patientId) {
      // Get the updated call time from back-end
      const response = await PatientService.fetchTimeData(token, patientId);
      console.log("Calltime response:", response);
      const callTime = response.rpm_call_time;

      console.log("Update last time of contact", utc_time);
      const updatedPatients = patients.map((patient) =>
        patient.patient_id === patientId
          ? {
              ...patient,
              last_contact: utc_time ? utc_time : "-",
              call_time: callTime,
            }
          : patient,
      );
      setPatients(updatedPatients);
    }
    // clear any selected patient for tracker
    setOpenTimeTracker(false);
    setSelectedPatientForTracker(null);
  };

  useEffect(() => {
    const keys = Object.keys(localStorage);
    const trackerKey = keys.find((key) => key.startsWith("timeTracker_"));
    if (trackerKey) {
      const sessionDataStr = localStorage.getItem(trackerKey);
      if (sessionDataStr) {
        try {
          const sessionData = JSON.parse(sessionDataStr);
          console.log("Found sessiondata: ", sessionData);
          if (Date.now() - sessionData.sessionStartTime < 2 * 60 * 60 * 1000) {
            const patientId = trackerKey.replace("timeTracker_", "");
            setSelectedPatientForTracker(patientId);
            setOpenTimeTracker(true);
          } else {
            localStorage.removeItem(trackerKey);
          }
        } catch (err) {
          console.error("Error parsing in-progress time tracker session", err);
        }
      }
    }
  }, []);

  //console.log("Filtered Patients:", filteredPatients);
  //console.log ("selectedEngagement:", selectedEngagement);

  return (
    <Box sx={{ flexGrow: 1, padding: theme.spacing(2) }}>
      {/* Render children (like VicuTabBar) if provided */}
      {children && <Box sx={{ mb: theme.spacing(2) }}>{children}</Box>}

      {/* Search Bar (temporarily disabled) */}
      {ENABLE_PATIENT_FILTER && (
        <Box sx={{ mb: theme.spacing(2) }}>
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Grid item xs={10}>
              <TextField
                label={t("Search Patients")}
                variant="outlined"
                fullWidth
                value={search}
                onChange={handleSearchChange}
              />
            </Grid>
            <Grid
              item
              xs={2}
              sx={{ display: "flex", justifyContent: "flex-end" }}
            >
              {refreshPatients && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={refreshPatients}
                  startIcon={<RefreshIcon />}
                  sx={{ ml: 2 }}
                >
                  {t("refresh")}
                </Button>
              )}
            </Grid>
          </Grid>
        </Box>
      )}

      <Grid container spacing={4}>
        {/* Patient List Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: theme.spacing(2), overflow: "hidden" }}>
            <Typography variant="h6">{t("Patient List")}</Typography>
            <TableContainer sx={{ maxHeight: tableHeight, overflowY: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ padding: "6px 8px" }}>{t("ID")}</TableCell>
                    <TableCell sx={{ padding: "6px 8px" }}>
                      {t("Name")}
                    </TableCell>
                    <TableCell sx={{ padding: "6px 8px" }}>
                      {t("CCM Plan")}
                    </TableCell>
                    {/* New Gauge Column */}
                    <TableCell sx={{ padding: "6px 8px" }}>
                      {t("Progress")}
                    </TableCell>
                    <TableCell sx={{ padding: "6px 8px" }}>
                      {t("Engagement History")}
                    </TableCell>
                    <TableCell sx={{ padding: "6px 8px" }}>
                      {t("Call Time")}
                    </TableCell>
                    <TableCell sx={{ padding: "6px 8px" }}>
                      {t("Last Contact")}
                    </TableCell>
                    <TableCell sx={{ padding: "6px 8px" }}>
                      {t("Video Call")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayPatients.map((patient: CCMPatient) => (
                    <TableRow key={patient.patient_id} sx={{ height: "36px" }}>
                      <TableCell sx={{ padding: "6px 8px" }}>
                        <Typography
                          sx={{
                            cursor: "pointer",
                            textDecoration: "underline",
                          }}
                          onClick={() => handleOpenPatientPlan(patient)}
                        >
                          {patient.patient_id}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ padding: "6px 8px" }}>
                        {patient?.last_name && patient?.first_name
                          ? `${patient.last_name}, ${patient.first_name}`
                          : "-"}
                      </TableCell>
                      <TableCell sx={{ padding: "6px 8px" }}>
                        {patient.patient_groups?.length > 0
                          ? patient.patient_groups.join(", ")
                          : "-"}
                      </TableCell>

                      {/* New Gauge Cell */}
                      <TableCell sx={{ padding: "6px 8px" }}>
                        {patient.measurement_days != null ? (
                          <Box
                            onClick={() =>
                              handleOpenCalendarDialog(patient.patient_id)
                            }
                            sx={{ cursor: "pointer" }}
                          >
                            <MeasurementGauge
                              measurementDays={patient.measurement_days}
                            />
                          </Box>
                        ) : (
                          "-"
                        )}
                      </TableCell>

                      <TableCell sx={{ padding: "6px 8px" }}>
                        {patient.patient_groups && (
                          <Stack direction="row" spacing={0.5}>
                            {Array.from({ length: RPM_HISTORY_DAYS }).map(
                              (_, index) => {
                                // Reverse the order: oldest (left) to newest (right)
                                const reversedIndex =
                                  RPM_HISTORY_DAYS - 1 - index;
                                const engagement =
                                  patient.engagementhistory?.[reversedIndex];
                                const engagementStatus =
                                  engagement?.engagement || "NO PLAN";
                                const isSelectable =
                                  engagementStatus !== "NO PLAN";
                                let dayLabel;
                                if (reversedIndex === 0) {
                                  dayLabel = t("Today");
                                } else if (reversedIndex === 1) {
                                  dayLabel = t("Yesterday");
                                } else {
                                  const dayDate = subDays(
                                    new Date(),
                                    reversedIndex,
                                  );
                                  dayLabel = format(dayDate, "EEEE");
                                }
                                return (
                                  <Tooltip
                                    key={index}
                                    title={`${dayLabel}: ${engagementStatus}`}
                                  >
                                    <Box
                                      onClick={
                                        isSelectable
                                          ? () =>
                                              handleTrafficLightClick(
                                                patient,
                                                engagement?.date,
                                              )
                                          : undefined
                                      }
                                      sx={{
                                        cursor: isSelectable
                                          ? "pointer"
                                          : "default",
                                      }}
                                    >
                                      {getTrafficLight(engagementStatus)}
                                    </Box>
                                  </Tooltip>
                                );
                              },
                            )}
                            <Tooltip title="View Trends">
                              <QueryStatsIcon
                                onClick={() =>
                                  handleOpenTrendDialog(patient.patient_id)
                                }
                                style={{
                                  cursor: "pointer",
                                  fontSize: 28,
                                  marginLeft: 30,
                                  color: "green",
                                }}
                              />
                            </Tooltip>
                            <Tooltip title="View All Measurements">
                              <FormatListNumberedIcon
                                onClick={() =>
                                  handleOpenListDialog(patient.patient_id)
                                }
                                style={{
                                  cursor: "pointer",
                                  fontSize: 28,
                                  marginLeft: 30,
                                  color: "secondary",
                                }}
                              />
                            </Tooltip>
                          </Stack>
                        )}
                      </TableCell>

                      <TableCell sx={{ padding: "6px 8px" }}>
                        <ElapsedTimeWidget
                          elapsedMinutes={patient.call_time}
                          patient_id={patient.patient_id}
                          organization_id={organization_id}
                        />
                      </TableCell>

                      <TableCell sx={{ padding: "6px 8px" }}>
                        {patient.patient_groups?.join(", ") &&
                        patient.last_contact
                          ? format(
                              new Date(patient.last_contact),
                              "MMM dd, yyyy HH:mm",
                            )
                          : "-"}
                      </TableCell>
                      <TableCell sx={{ padding: "6px 8px" }}>
                        <Tooltip title={t("Start Video Call")}>
                          <span>
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handleOpenVideoCall(patient)}
                              disabled={!token}
                            >
                              <VideocamIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Engagement Details Dialog */}
      {openEngagementDialog && selectedEngagementPatient && (
        <EngagementDetailsDialog
          open={openEngagementDialog}
          onClose={handleCloseEngagementDialog}
          patient_id={selectedEngagementPatient}
          date={selectedEngagementDate}
          token={token}
        />
      )}

      {selectedPatientId && selectedPatient && (
        <MeasurementTrendDialog
          patient={selectedPatient}
          conditionType={
            selectedPatient.patient_groups
              ? selectedPatient.patient_groups.join(", ")
              : selectedPatient.patient_groups
          }
          organization_id={organization_id}
          token={cookies.access_token}
          open={openTrendDialog}
          onClose={handleCloseTrendDialog}
        />
      )}

      {selectedPatientId && selectedPatient && (
        <MeasurementListDialog
          patientId={selectedPatientId}
          conditionType={
            selectedPatient.patient_groups
              ? selectedPatient.patient_groups.join(", ")
              : selectedPatient.patient_groups
          }
          organization_id={organization_id}
          token={cookies.access_token}
          open={openListDialog}
          onClose={handleCloseListDialog}
        />
      )}

      {openCalendarDialog && selectedPatientId && (
        <MeasurementCalendarDialog
          patientId={selectedPatientId}
          organization_id={organization_id}
          token={cookies.access_token}
          open={openCalendarDialog}
          onClose={handleCloseCalendarDialog}
          engagementHistory={selectedPatient?.engagementhistory}
        />
      )}
      {selectedPatientForTracker && (
        <TimeTracker
          patientId={selectedPatientForTracker}
          open={openTimeTracker}
          onClose={handleTimeTrackerClose}
        />
      )}

      <Dialog
        open={openVideoCall}
        onClose={handleCloseVideoCall}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedPatientForVideoCall
            ? `${t("Video Call")}: ${selectedPatientForVideoCall.last_name || ""}${selectedPatientForVideoCall.last_name && selectedPatientForVideoCall.first_name ? ", " : ""}${selectedPatientForVideoCall.first_name || selectedPatientForVideoCall.patient_id}`
            : t("Video Call")}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, minHeight: 500 }}>
            {selectedPatientForVideoCall && token ? (
              <JitsiMeeting
                domain="videocall.sentioweb.com"
                roomName={selectedPatientForVideoCall.patient_id}
                configOverwrite={{
                  startWithAudioMuted: true,
                  disableModeratorIndicator: true,
                  startScreenSharing: true,
                  enableEmailInStats: false,
                  context: {
                    user: {
                      name: meetingDisplayName,
                      email: meetingEmail,
                    },
                  },
                }}
                interfaceConfigOverwrite={{
                  DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
                }}
                userInfo={{
                  displayName: meetingDisplayName,
                  email: meetingEmail,
                }}
                onReadyToClose={handleCloseVideoCall}
                getIFrameRef={(iframeRef) => {
                  iframeRef.style.height = "500px";
                  iframeRef.style.width = "100%";
                  iframeRef.setAttribute(
                    "allow",
                    "camera *; microphone *; fullscreen; display-capture; autoplay",
                  );
                  iframeRef.setAttribute("allowusermedia", "true");
                }}
                jwt={token}
              />
            ) : (
              <Typography>{t("Unable to start video call")}</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseVideoCall}>{t("close")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CCMCentral;
